import {
    APP_CONTENT_RESIZE_EVENT,
    STATE_TRANSITION_START_EVENT,
    SIMULATE_FULLSCREEN,
    STOP_SIMULATE_FULLSCREEN,
    NEW_USER_FLOW,
    SHOW_MISSING_BILLING_INFO_BANNER,
    REMOVE_MISSING_BILLING_INFO_BANNER,
    SHOW_OVERDUE_INVOICE_BANNER,
    REMOVE_OVERDUE_INVOICE_BANNER,
    SHOW_ORGANIZATION_ALERTS,
    HIDE_ORGANIZATION_ALERTS,
    SHOW_ADJUSTING_LICENSES,
    PROJECT_CHANGED
} from 'services/StudioEvents';
import {
    TOP_LEVEL_NETWORK_ERROR_STATE_KEY,
    TOP_LEVEL_NETWORK_ERROR_VISIBLE_STATE_KEY
} from 'services/api/ResourceStream';
import { CREATE_MEMBERSHIP } from 'services/Permissions';
import { LICENSE_ACTIVATION_TIMEOUT_MS } from 'services/BillingCheckHelper';
import { reflowCharts } from 'services/utils';
import { States } from '../../app.router';


/* @ngInject */
export default class LayoutController {
    constructor($timeout, $scope, $state, StateService, AppMenu, $rootScope, RoleManager, BillingCheckHelper, ToastService, StorageService, ProjectManager) {
        this.$timeout = $timeout;
        this.appMenu = AppMenu;
        this.navCollapsed = false;
        this.$scope = $scope;
        this.$state = $state
        this.$rootScope = $rootScope;
        this.RoleManager = RoleManager;
        this.StateService = StateService;
        this.BillingCheckHelper = BillingCheckHelper
        this.ToastService = ToastService
        this.StorageService = StorageService
        this.ProjectManager = ProjectManager
    }

    $onInit() {
        this.isMobileNav = null;
        this.removeEventListener = this.$scope.$root.$on(
            STATE_TRANSITION_START_EVENT,
            () => {
                this.hideMobileNav();
            }
        );
        this.removeResizeListener = this.$scope.$root.$on(
            APP_CONTENT_RESIZE_EVENT,
            () => {
                reflowCharts();
            }
        );
        this.removeSimulateFullscreenListener = this.$scope.$root.$on(
            SIMULATE_FULLSCREEN, () => {
                this.navCollapsed = true
            }
        )
        this.removeStopSimulateFullscreenListener = this.$scope.$root.$on(
            STOP_SIMULATE_FULLSCREEN, () => {
                this.navCollapsed = false
            }
        )
        this.newUserFlow = false
        this.$rootScope.$on(NEW_USER_FLOW, () => {
            this.newUserFlow = true
        })

        this.$rootScope.$on(SHOW_ORGANIZATION_ALERTS, () => {
            this.showingOrganizationAlerts = true
        })

        this.$rootScope.$on(HIDE_ORGANIZATION_ALERTS, () => {
            this.showingOrganizationAlerts = false
        })

        setTimeout(() => {
            this.BillingCheckHelper.check()
        }, 1000); // No need to rush this

        this.missingBillingInfo = false
        this.overdueInvoice = false

        this.$rootScope.$on(SHOW_MISSING_BILLING_INFO_BANNER, () => {
            this.missingBillingInfo = true
            this.$rootScope.$applyAsync()
        })

        this.$rootScope.$on(REMOVE_MISSING_BILLING_INFO_BANNER, () => {
            this.missingBillingInfo = false
            this.$rootScope.$applyAsync()
        })

        this.$rootScope.$on(SHOW_OVERDUE_INVOICE_BANNER, () => {
            this.overdueInvoice = true
            this.$rootScope.$applyAsync()
        })

        this.$rootScope.$on(REMOVE_OVERDUE_INVOICE_BANNER, () => {
            this.overdueInvoice = false
            this.$rootScope.$applyAsync()
        })

        this.$rootScope.$on(SHOW_ADJUSTING_LICENSES, () => {
            this.licensesAreBeingActivated = true
            this.$rootScope.$applyAsync()

            // Start a polling interval to check the status of the license activation
            this.licensesActivationInterval = setInterval(() => {
                const activationInProgress = this.StorageService.getItem('ADJUSTING_LICENSES')
                if (!activationInProgress) {
                    // If the activation is no longer in progress, clear the interval
                    clearInterval(this.licensesActivationInterval)
                    this.licensesAreBeingActivated = false
                    this.failedLicenseActivation = false
                    return
                }
                // Check if the activation is for the current organization
                if (activationInProgress.organizationId !== this.ProjectManager.currentProject.organizationId) {
                    // If the activation is for a different organization, clear the interval
                    clearInterval(this.licensesActivationInterval)
                    this.licensesAreBeingActivated = false
                    this.failedLicenseActivation = false
                    return
                }

                // Perform the license activation check
                this.performLicenseActivationCheck()
            }, 5000) // Check every 5 seconds
        })

        // Flag used to show/hide the licenses activation banner
        this.licensesAreBeingActivated = false

        // Flag used to show/hide the license activation error banner
        // This is shown only after the LICENSE_ACTIVATION_TIMEOUT_MS verification
        // window has elapsed without the new licenses appearing. Until then we keep
        // polling and show the "activating" banner instead.
        // It will remain visible until the user uses the "X" button to close it
        // or the licenses are activated
        this.failedLicenseActivation = false

        // Check on load if the user is in the process of activating licenses
        this.checkActivationInProgress()

        this.$rootScope.$on(PROJECT_CHANGED, () => {
            clearInterval(this.licensesActivationInterval)
            // Check if the user is in the process of activating licenses
            this.checkActivationInProgress()
        })
    }

    $onDestroy() {
        this.removeEventListener();
        this.removeResizeListener();
        this.removeSimulateFullscreenListener();
        this.removeStopSimulateFullscreenListener();
    }

    get isOrganizationAdmin() {
        return this.RoleManager.hasOrganizationPermissionTo(CREATE_MEMBERSHIP)
    }

    get hasNetworkError() {
        return (
            this.StateService.getItem(
                TOP_LEVEL_NETWORK_ERROR_VISIBLE_STATE_KEY,
                false
            ) &&
            this.StateService.getItem(TOP_LEVEL_NETWORK_ERROR_STATE_KEY, false)
        );
    }

    // Avoid showing billing banners when viewing the billing page
    get notShowingBilling() {
        return this.$state.current.name !== States.ORGANIZATION_BILLING
    }

    // Temporarily hide the billing warning (until Studio is refreshed)
    hideWarning() {
        this.missingBillingInfo = false
        this.overdueInvoice = false
    }

    checkActivationInProgress() {
        const activationInProgress = this.StorageService.getItem('ADJUSTING_LICENSES')
        if (activationInProgress) {
            // Check if the activation is for the current organization
            if (activationInProgress.organizationId === this.ProjectManager.currentProject?.organizationId) {    
                const timestamp = activationInProgress.timestamp
                // Check if the activation is still within the verification window
                const now = new Date().getTime()
                if (now - timestamp < LICENSE_ACTIVATION_TIMEOUT_MS) {
                    // Show the banner and start the interval to check
                    this.$rootScope.$broadcast(SHOW_ADJUSTING_LICENSES)
                } else {
                    this.performLicenseActivationCheck()
                }
            } else {
                // If the activation is for a different organization, clear the state
                clearInterval(this.licensesActivationInterval)
                this.licensesAreBeingActivated = false
                this.failedLicenseActivation = false
            }
        }
    }

    performLicenseActivationCheck() {
        this.BillingCheckHelper.checkIfLicensesHaveBeenActivated().then((status) => {
            if (status === 'ACTIVATED') {
                this.licensesAreBeingActivated = false
                this.ToastService.showSimpleText("Licenses are now available!", {
                    hideDelay: 6000,
                    position: 'top',
                    toastClass: 'dt-toast-success'
                });
                this.$rootScope.$applyAsync()
                clearInterval(this.licensesActivationInterval)
            }
            if (status === 'FAILED') {
                this.failedLicenseActivation = true
                clearInterval(this.licensesActivationInterval)
            }
        }).catch((error) => {
            console.error(`Error checking license activation status: ${error}`) // eslint-disable-line no-console
        })
    }

    // Shared logic for both the in progress and failed activation banners
    removeLicensesBeingActivated() {
        this.StorageService.removeItem('ADJUSTING_LICENSES')
        // Clear the interval if it exists
        if (this.licensesActivationInterval) {
            clearInterval(this.licensesActivationInterval)
        }
        this.licensesAreBeingActivated = false
        this.failedLicenseActivation = false
    }

    openBilling() {
        this.$state.go(States.ORGANIZATION_BILLING);
    }

    showMobileNav() {
        this.isMobileNav = true;
    }

    hideMobileNav() {
        this.isMobileNav = false;
    }
}
